import React, { useEffect, useRef } from "react";
import {
  CANONICAL_ASSET_ORDER,
  isStudentAssetType,
  type StudentAssetType,
  type Tier,
} from "../../../engine/sequenceEngine";
import { useT } from "../../../stores/uiLang";

// Run E3 B4: picker text follows the UI language (EN text unchanged).
const DP_T = {
  en: {
    phases: {
      prepare: { label: "Prepare", sub: "Vocabulary (Apply cards)" },
      watch: { label: "Watch", sub: "Animated episode" },
      read: { label: "Read", sub: "Scene Reader" },
      check: { label: "Check", sub: "Embedded formatives" },
      apply: { label: "Apply", sub: "Chapter Activity" },
      assess: { label: "Assess", sub: "Summative quiz (Tiers 3–4)" },
    } as Record<string, { label: string; sub: string }>,
    whatAssigned: "What gets assigned",
    selectAll: "Select all",
    clearAll: "Clear all",
    help: "Choose which parts of the chapter ship to students. Teacher materials are always yours and are never pushed to students.",
    summativeOnly: "Summative is available for Tiers 3–4 only.",
    nothing: "Nothing selected — students would receive an empty assignment. Use “Select all” or pick at least one part.",
  },
  es: {
    phases: {
      prepare: { label: "Preparar", sub: "Vocabulario (tarjetas de aplicar)" },
      watch: { label: "Mirar", sub: "Episodio animado" },
      read: { label: "Leer", sub: "Lector de escenas" },
      check: { label: "Revisar", sub: "Preguntas dentro de la lección" },
      apply: { label: "Aplicar", sub: "Actividad del capítulo" },
      assess: { label: "Evaluar", sub: "Prueba final (Niveles 3 y 4)" },
    } as Record<string, { label: string; sub: string }>,
    whatAssigned: "Qué se asigna",
    selectAll: "Elegir todo",
    clearAll: "Quitar todo",
    help: "Elige qué partes del capítulo reciben los estudiantes. Los materiales para docentes son solo tuyos y nunca se envían a los estudiantes.",
    summativeOnly: "La prueba final solo está disponible para los Niveles 3 y 4.",
    nothing: "No hay nada elegido: los estudiantes recibirían una tarea vacía. Usa “Elegir todo” o elige al menos una parte.",
  },
};

/**
 * DeploymentPicker — the teacher's "what ships to students" control (spec §5).
 *
 * PATH-AGNOSTIC by design: this component knows nothing about the native modal
 * or the LTI surface. It takes a tier + a controlled selection and emits a
 * { presetId, assets } payload. Both the native assign flow and (next chunk)
 * the LTI assign surface mount THIS same component — parity is structural, not
 * copied (§7 parity gate).
 *
 * Composition model (v1): teachers compose directly from the GRR-phase toggle
 * set. The selection defaults to ALL available toggles ON (Assess auto-gated to
 * T3/T4); a Select all / Clear all pair sits above the list. Named saved-config
 * presets can come later — the capability already lives in the toggles, so
 * `presetId` stays in the emit contract (always null for now) for forward
 * compatibility.
 *
 * HARD-BLOCK (§5 Layer 3): the selectable universe is `GRR_PHASES`, whose
 * `assets` are typed `readonly StudentAssetType[]`. A teacher-only literal there
 * is a COMPILE error. The 6 teacher-only assets are not toggled, not listed, not
 * disabled-shown — unselectable by construction, mirroring createSequence()'s
 * TeacherAssetRejectedError. A defensive isStudentAssetType filter on emit is
 * belt-and-braces.
 *
 * Summative is a student asset but tier-gated (tiersOnly T3/T4); on T1/T2 its
 * toggle is shown disabled and can never enter the emitted asset list.
 */

// The emit contract. Mirrors what the assign flow persists as `deployment`
// (null ⇒ full chapter / today's proven CANONICAL_STEPS loop).
export interface DeploymentSelection {
  /** Reserved for future named saved-config presets; null in v1. */
  presetId: string | null;
  /** Resolved student assets in CANONICAL_ASSET_ORDER. */
  assets: StudentAssetType[];
}

interface GrrPhase {
  key: string;
  label: string;
  /** GRR verb phase label shown above the asset. */
  sub: string;
  /**
   * The student assets this phase deploys. Typed StudentAssetType[] so a
   * teacher-only literal here is a COMPILE error — the structural hard-block.
   */
  assets: readonly StudentAssetType[];
  /** When set, the phase only exists for these tiers (summative = T3/T4). */
  tiersOnly?: readonly Tier[];
}

// Canonical (sequence) order — matches CANONICAL_STEPS, so the toggle column
// reads top-to-bottom in the same order the student experiences the loop.
const GRR_PHASES: readonly GrrPhase[] = [
  { key: "prepare", label: "Prepare", sub: "Vocabulary (Apply cards)", assets: ["vocab_apply"] },
  { key: "watch", label: "Watch", sub: "Animated episode", assets: ["episode_video"] },
  { key: "read", label: "Read", sub: "Scene Reader", assets: ["scene_reader"] },
  { key: "check", label: "Check", sub: "Embedded formatives", assets: ["formatives"] },
  {
    key: "apply",
    label: "Apply",
    // Chapter Activity only for now. The Student Activity Booklet (asset 11)
    // has 0 rows / no source yet — do NOT promise it in the label until it
    // lands. TODO(booklet): add "activity_booklet" to this phase's assets and
    // restore the Booklet wording once the asset is built & seeded.
    sub: "Chapter Activity",
    assets: ["chapter_activity"],
  },
  {
    key: "assess",
    label: "Assess",
    sub: "Summative quiz (Tiers 3–4)",
    assets: ["summative_quiz"],
    tiersOnly: ["T3", "T4"],
  },
] as const;

const phaseAvailable = (p: GrrPhase, tier: Tier): boolean =>
  !p.tiersOnly || p.tiersOnly.includes(tier);

/** All GRR phase keys available at a given tier (the "Select all" target). */
function allAvailableKeys(tier: Tier): Set<string> {
  return new Set(GRR_PHASES.filter((p) => phaseAvailable(p, tier)).map((p) => p.key));
}

/** Resolve a set of active GRR phase keys → ordered, tier-valid student assets. */
function resolveAssets(activeKeys: Set<string>, tier: Tier): StudentAssetType[] {
  const present = new Set<StudentAssetType>();
  for (const p of GRR_PHASES) {
    if (!activeKeys.has(p.key)) continue;
    if (!phaseAvailable(p, tier)) continue; // tier gate (drops summative on T1/T2)
    for (const a of p.assets) present.add(a);
  }
  // Canonical order + defensive type-boundary filter (hard-block, belt & braces).
  return CANONICAL_ASSET_ORDER.filter(
    (a) => present.has(a) && isStudentAssetType(a),
  ) as StudentAssetType[];
}

/** Which GRR phases are currently active, derived from the emitted asset list. */
function activePhaseKeys(assets: StudentAssetType[]): Set<string> {
  const set = new Set(assets);
  const keys = new Set<string>();
  for (const p of GRR_PHASES) {
    if (p.assets.some((a) => set.has(a))) keys.add(p.key);
  }
  return keys;
}

const sameKeys = (a: Set<string>, b: Set<string>): boolean =>
  a.size === b.size && [...a].every((k) => b.has(k));

const sameAssets = (a: StudentAssetType[], b: StudentAssetType[]): boolean =>
  a.length === b.length && a.every((x, i) => x === b[i]);

interface Props {
  /** Current assignment tier — gates the summative phase. */
  tier: Tier;
  value: DeploymentSelection;
  onChange: (next: DeploymentSelection) => void;
  disabled?: boolean;
}

export default function DeploymentPicker({ tier, value, onChange, disabled }: Props) {
  const t = useT(DP_T);
  const active = activePhaseKeys(value.assets);
  const prevTier = useRef<Tier>(tier);

  // Tier reconciliation on tier change. If the selection was "all available"
  // for the OLD tier (the Select-all state), re-select-all for the new tier so
  // a newly-available phase (Assess at T3) joins and a now-invalid one (Assess
  // at T1) drops. Otherwise reconcile from the active phases, which still drops
  // any now-invalid asset via the tier gate. Only emits on real change.
  useEffect(() => {
    const wasAllOn = sameKeys(active, allAvailableKeys(prevTier.current));
    const nextKeys = wasAllOn ? allAvailableKeys(tier) : active;
    const reconciled = resolveAssets(nextKeys, tier);
    prevTier.current = tier;
    if (!sameAssets(reconciled, value.assets)) {
      onChange({ presetId: value.presetId, assets: reconciled });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier]);

  const selectAll = () => {
    if (disabled) return;
    onChange({ presetId: null, assets: resolveAssets(allAvailableKeys(tier), tier) });
  };

  const clearAll = () => {
    if (disabled) return;
    onChange({ presetId: null, assets: [] });
  };

  const togglePhase = (key: string) => {
    if (disabled) return;
    const next = new Set(active);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange({ presetId: null, assets: resolveAssets(next, tier) });
  };

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <label className="block text-sm font-medium text-gray-700">
          {t.whatAssigned}
        </label>
        <div className="flex items-center gap-3 text-xs">
          <button
            type="button"
            onClick={selectAll}
            disabled={disabled}
            className="font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
          >
            {t.selectAll}
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={clearAll}
            disabled={disabled}
            className="font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            {t.clearAll}
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-0.5">
        {t.help}
      </p>

      {/* À-la-carte GRR-phase toggles */}
      <div className="mt-2 rounded-md border border-gray-200 divide-y divide-gray-100">
        {GRR_PHASES.map((p) => {
          const available = phaseAvailable(p, tier);
          const checked = active.has(p.key) && available;
          const rowDisabled = disabled || !available;
          return (
            <label
              key={p.key}
              className={`flex items-start gap-3 px-3 py-2 ${
                rowDisabled ? "opacity-60" : "cursor-pointer hover:bg-gray-50"
              }`}
              title={
                !available
                  ? t.summativeOnly
                  : undefined
              }
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                checked={checked}
                onChange={() => togglePhase(p.key)}
                disabled={rowDisabled}
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  {t.phases[p.key]?.label ?? p.label}
                </span>
                <span className="block text-xs text-gray-500">{t.phases[p.key]?.sub ?? p.sub}</span>
              </span>
            </label>
          );
        })}
      </div>

      {/* Empty-state guard mirrors the engine's EmptySequenceError. Submit is
          blocked upstream when assets is empty. */}
      {value.assets.length === 0 && (
        <p className="mt-2 text-xs text-amber-600">
          {t.nothing}
        </p>
      )}
    </div>
  );
}

/** Default selection: everything available for the tier, no named preset. */
export function defaultDeployment(tier: Tier): DeploymentSelection {
  return { presetId: null, assets: resolveAssets(allAvailableKeys(tier), tier) };
}
